import {
  getCategories,
  getProducts,
  getProduct,
  getProductStock,
  getProductNotice,
} from "../../services/productService";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type CategoryInfo = {
  code: string;
  name: string;
};

export interface CategoryType extends CategoryInfo {
  subcategories: CategoryInfo[];
}

export interface ProductType {
  Code?: string;
  TVATaux?: number;
  createdAt?: string;
  Description?: string;
  updatedAt?: string;
  Reference: string;
  Category?: CategoryInfo;
  Designation?: string;
  PrixHT: number;
  SubCategory?: CategoryInfo;
  ImageUrl: string;
  FSFilePath?: string;
  FTFilePath?: string;
  StockQuantity?: number;
}

type ProductsType = {
  items: ProductType[];
  targetItem: {
    item?: ProductType;
    stock?: string;
    lastEvaluatedKey: string;
    noticeFS?: string;
    noticeFT?: string;
  };
};

type CartProduct = {
  productCode: string;
  quantity: number;
};

interface ProductState {
  products: ProductsType;
  categories: CategoryType[];
  shoppingCart: CartProduct[];
  loading: boolean;
  error: string | null;
}

const initialState: ProductState = {
  products: {
    items: [],
    targetItem: {
      item: undefined,
      stock: undefined,
      lastEvaluatedKey: "",
    },
  },
  shoppingCart: [],
  categories: [],
  loading: false,
  error: null,
};

const productSlice = createSlice({
  name: "products",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getProducts.fulfilled,
        (state, action: PayloadAction<ProductsType>) => {
          state.loading = false;
          state.products = action.payload;
        }
      )
      .addCase(getProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(getProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getProduct.fulfilled,
        (state, action: PayloadAction<ProductType>) => {
          state.loading = false;
          state.products.targetItem = {
            ...state.products.targetItem,
            item: action.payload,
          };
        }
      )
      .addCase(getProduct.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(getProductStock.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(
        getProductStock.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.loading = false;
          state.products.targetItem = {
            ...state.products.targetItem,
            stock: action.payload,
          };
        }
      )
      .addCase(
        getProductStock.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        }
      )

      .addCase(getProductNotice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(
        getProductNotice.fulfilled,
        (state, action: PayloadAction<{ type: "ft" | "fs"; url: string }>) => {
          state.loading = false;
          if (action.payload.type === "fs") {
            state.products.targetItem = {
              ...state.products.targetItem,
              noticeFS: action.payload.url,
            };
          } else {
            state.products.targetItem = {
              ...state.products.targetItem,
              noticeFT: action.payload.url,
            };
          }
        }
      )
      .addCase(
        getProductNotice.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        }
      )

      .addCase(getCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getCategories.fulfilled,
        (state, action: PayloadAction<CategoryType[]>) => {
          state.loading = false;
          state.categories = action.payload;
        }
      )
      .addCase(getCategories.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default productSlice.reducer;
